import React, { useEffect, useState } from "react";
import FieldEditor from "./FieldEditor";
import { FormFieldDto } from "../../Models/FormFieldDto";
import { FormDto } from "../../Models/FormDto";
import Editor from "../Common/Editor";
import FormApi from "../../Api/FormApi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Select } from "flowbite-react";
import SitePageApi from "../../Api/SitePageApi";
import { SitePage } from "../../Models/SitePage";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import { QBReferenceItemDto } from "../../Models/QBReferenceItemDto";
import CommonApi from "../../Api/CommonApi";
import EventApi from "../../Api/EventApi";
import { Event } from "../../Models/Event";
import axios from "axios";

const emptyField = (sortOrder: number): FormFieldDto => ({
  label: "",
  fieldType: "SingleText",
  isRequired: false,
  sortOrder,
  options: [],
  description: "",
  defaultValue: "",
});

const FormBuilderPage: React.FC = () => {
  const { id } = useParams();
  const formId = id ? parseInt(id) : undefined;
  const [searchParams] = useSearchParams();
  const copyFromIdRaw = searchParams.get("copyFrom");
  const copyFromId = copyFromIdRaw ? parseInt(copyFromIdRaw, 10) : undefined;
  const { userModel } = useAppSelector((state) => state.user);
  const [quillValue, setQuillValue] = useState("");
  const [form, setForm] = useState<FormDto>({
    name: "",
    title: "",
    introDescription: "",
    confirmationHTML: "",
    isActive: true,
    startDate: "",
    endDate: "",
    createdById: 0,
    pageId: 0,
    fields: [],
  });
  const [sitePages, setSitePages] = useState<SitePage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<number>(0);
  const [qbItems, setQbItems] = useState<QBReferenceItemDto[]>([]);
  const [eventList, setEventList] = useState<Event[]>([]);
  const [responseCount, setResponseCount] = useState(0);

  const navigate = useNavigate();

  const cloneFormForCreate = (source: FormDto): FormDto => ({
    ...source,
    formId: undefined,
    name: source.name ? `${source.name} (Copy)` : "",
    title: source.title ? `${source.title} (Copy)` : "",
    isActive: false,
    fields: (source.fields ?? []).map((f) => ({
      ...f,
      fieldId: undefined,
      options: (f.options ?? []).map((o) => ({ ...o })),
      userTypes: (f.userTypes ?? []).map((u) => ({ ...u })),
    })),
  });

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      // Redirect to home or unauthorized page
      navigate("/"); // or another route like "/unauthorized"
    }
  }, [userModel, navigate]);

  useEffect(() => {
    CommonApi.getQBItems()
      .then((res) => setQbItems(res.data))
      .catch((err) => toast.error("Error loading QB Items: " + err.message));
    EventApi.getEventList()
      .then((res) => setEventList(res.data))
      .catch((err) => toast.error("Error loading QB Items: " + err.message));
  }, []);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      // Redirect to home or unauthorized page
      navigate("/"); // or another route like "/unauthorized"
    }
  }, [userModel, navigate]);
  useEffect(() => {
    SitePageApi.getSitePageList()
      .then((res) => setSitePages(res.data))
      .catch((err) => console.error("Error loading site pages", err));
  }, []);

  useEffect(() => {
    if (!formId && !copyFromId) return;

    const idToLoad = formId ?? copyFromId!;
    FormApi.getForm(idToLoad)
      .then((res) => {
        const source = res.data;
        const model = formId ? source : cloneFormForCreate(source);
        setForm(model);
        setSelectedPageId(model.pageId ?? 0);
        if (model?.confirmationHTML) {
          setQuillValue(
            model.confirmationHTML.replace(/(?:\r\n|\r|\n)/g, "<br>")
          );
        }
      })
      .catch((err) => toast.error("Error loading form: " + err.message));
  }, [formId, copyFromId]);

  useEffect(() => {
    if (!formId) {
      setResponseCount(0);
      return;
    }
    FormApi.getResponses(formId)
      .then((res) => setResponseCount(res.data?.length ?? 0))
      .catch(() => setResponseCount(0));
  }, [formId]);

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [...prev.fields, emptyField(prev.fields.length)],
    }));
  };

  const updateField = (index: number, changes: Partial<FormFieldDto>) => {
    setForm((prev) => {
      const updated = [...prev.fields];
      updated[index] = { ...updated[index], ...changes };
      return { ...prev, fields: updated };
    });
  };

  const removeField = (index: number) => {
    const updated = form.fields.filter((_, i) => i !== index);
    updated.forEach((f, i) => (f.sortOrder = i));
    setForm((prev) => ({ ...prev, fields: updated }));
  };

  const moveField = (index: number, direction: number) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= form.fields.length) return;
    const updated = [...form.fields];
    const [moved] = updated.splice(index, 1);
    updated.splice(newIndex, 0, moved);
    updated.forEach((f, i) => (f.sortOrder = i));
    setForm((prev) => ({ ...prev, fields: updated }));
  };

  const saveForm = () => {
    const clamp = (value: string | null | undefined, max: number) => {
      if (!value) return value ?? "";
      return value.length > max ? value.slice(0, max) : value;
    };
    const toFiniteNumberOrNull = (value: number | null | undefined) =>
      typeof value === "number" && Number.isFinite(value) ? value : null;
    const toFiniteIntOrNull = (value: number | null | undefined) =>
      typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : null;

    if (form.startDate && form.endDate) {
      const start = new Date(form.startDate);
      const end = new Date(form.endDate);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end < start) {
        toast.error("End date cannot be before start date.");
        return;
      }
    }

    const sanitizedFields: FormFieldDto[] = (form.fields ?? []).map((field) => ({
      ...field,
      label: clamp(field.label, 200),
      fieldType: clamp(field.fieldType, 50),
      defaultValue:
        field.fieldType === "BoothPicker"
          ? clamp(field.defaultValue, 2000)
          : clamp(field.defaultValue, 200),
      price: toFiniteNumberOrNull(field.price),
      userAddCount: toFiniteIntOrNull(field.userAddCount),
      userTypes: (field.userTypes ?? []).map((u) => ({
        ...u,
        price: typeof u.price === "number" && Number.isFinite(u.price) ? u.price : 0,
        qbRefId: toFiniteIntOrNull(u.qbRefId),
      })),
      options: (field.options ?? []).map((opt) => ({
        ...opt,
        name: clamp(opt.name, 500),
        description: opt.description,
        userAddType: clamp(opt.userAddType, 50),
        amount: typeof opt.amount === "number" && Number.isFinite(opt.amount) ? opt.amount : 0,
        qbRefId: toFiniteIntOrNull(opt.qbRefId),
        userAddCount: toFiniteIntOrNull(opt.userAddCount),
        userTypes: (opt.userTypes ?? []).map((u) => ({
          ...u,
          price: typeof u.price === "number" && Number.isFinite(u.price) ? u.price : 0,
          qbRefId: toFiniteIntOrNull(u.qbRefId),
        })),
      })),
    }));

    const updatedForm: FormDto = {
      formId: form.formId,
      name: form.name ?? "",
      title: form.title ?? "",
      introDescription: form.introDescription ?? "",
      confirmationHTML: quillValue ?? "",
      isActive: !!form.isActive,
      startDate: form.startDate,
      endDate: form.endDate,
      createdById: userModel?.id || 0,
      pageId: selectedPageId,
      eventId: form.eventId ?? null,
      fields: sanitizedFields,
    };
    FormApi.createOrUpdateForm(updatedForm)
      .then(() => {
        toast.success("Success!");
        navigate("/forms/manage");
      })
      .catch((err) => {
        const apiMessage = axios.isAxiosError(err)
          ? (typeof err.response?.data === "string"
              ? err.response?.data
              : err.response?.data?.message ?? err.response?.data?.detail)
          : undefined;
        toast.error(`Error saving form: ${apiMessage || err.message}`);
      });
  };

  return (
    <div className="p-6 w-full text-left">
      <h1 className="text-2xl font-bold mb-4">
        {formId ? "Edit" : "Create"} Form
      </h1>

      {formId && responseCount > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-amber-900">
          <div className="font-semibold">
            This form already has {responseCount} response{responseCount === 1 ? "" : "s"}.
          </div>
          <div className="text-sm mt-1">
            To avoid changing a live form, consider creating a copy first and editing the copy.
          </div>
          <div className="mt-2">
            <button
              type="button"
              className="px-3 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
              onClick={() => navigate(`/forms/new?copyFrom=${formId}`)}
            >
              Copy This Form First
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4 mb-6">
        {/* Form fields with captions */}
        <div>
          <label className="block font-medium mb-1">Form Name</label>
          <input
            className="w-full p-2 border rounded"
            placeholder="Form Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Title</label>
          <input
            className="w-full p-2 border rounded"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Into Description</label>
          <textarea
            className="w-full p-2 border rounded"
            placeholder="Description"
            value={form.introDescription || ""}
            onChange={(e) =>
              setForm({ ...form, introDescription: e.target.value })
            }
            rows={3}
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Start Date</label>
          <input
            type="date"
            className="p-2 border rounded"
            value={form.startDate?.split("T")[0] || ""}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
        </div>

        <div>
          <label className="block font-medium mb-1">End Date</label>
          <input
            type="date"
            className="p-2 border rounded"
            value={form.endDate?.split("T")[0] || ""}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Is Active</label>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <span className="ml-2">Yes</span>
          </label>
        </div>
        <div>
          <label className="block font-medium mb-1">
            Select Page to Show On (Optional)
          </label>
          <Select
            value={selectedPageId}
            onChange={(e) => setSelectedPageId(Number(e.target.value))}
          >
            <option value="">-- Choose a Page --</option>
            {sitePages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.title}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block font-medium mb-1">Associated Event</label>
          <Select
            value={form.eventId ?? ""}
            onChange={(e) =>
              setForm({ ...form, eventId: parseInt(e.target.value) || null })
            }
          >
            <option value="">-- Select Event --</option>
            {eventList.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="block font-medium mb-1">Confirmation Email</label>
          <Editor
            id="description"
            name="description"
            value={quillValue}
            className="w-full"
            changedValue={setQuillValue}
          />
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-2">
        Fields - Note: Personal information known already, do not add fields for
        that.
      </h2>

      {form.fields.map((field, i) => (
        <FieldEditor
          key={`field-${i}`}
          field={field}
          index={i}
          onChange={updateField}
          onRemove={removeField}
          onMove={moveField}
          qbItems={qbItems}
        />
      ))}

      <div className="mt-4 flex space-x-2">
        <button
          className="px-4 py-2 bg-green-600 text-white rounded"
          onClick={addField}
        >
          Add Field
        </button>
      </div>
      <div className="mt-4 flex space-x-2">
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded"
          onClick={saveForm}
        >
          Save Form
        </button>
      </div>
    </div>
  );
};

export default FormBuilderPage;
